import { useState, useEffect, useRef } from "react";
import { Plus, Pencil, Trash2, Eye, Images, Upload, Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import AdminLayout from "@/components/layout/AdminLayout";
import axios from "axios";
import { API } from "@/App";
import AdminPagination from "@/components/admin/AdminPagination";

const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  if (url.startsWith('/uploads/')) {
    return `${backendUrl}/api${url}`;
  }
  return `${backendUrl}${url}`;
};

export default function AdminGalerias() {
  const [galleries, setGalleries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [total, setTotal] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedGallery, setSelectedGallery] = useState(null);
  const [saving, setSaving] = useState(false);
  const [mediaList, setMediaList] = useState([]);
  const [mediaDialogOpen, setMediaDialogOpen] = useState(false);
  const [mediaSelectMode, setMediaSelectMode] = useState("cover"); // "cover" or "images"
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    cover_image_id: "",
    cover_image_url: "",
    image_ids: [],
    images: [],
    location: "",
    date_taken: "",
    status: "draft",
  });

  const token = localStorage.getItem("session_token");
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchGalleries();
  }, [page, limit]);

  const fetchGalleries = async () => {
    try {
      const response = await axios.get(`${API}/galleries?skip=${(page - 1) * limit}&limit=${limit}`, { headers });
      setGalleries(response.data.items);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error("Error fetching galleries:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMedia = async () => {
    try {
      const response = await axios.get(`${API}/media?limit=100`, { headers });
      setMediaList(response.data.items);
    } catch (error) {
      console.error("Error fetching media:", error);
    }
  };

  const handleOpenDialog = (gallery = null) => {
    if (gallery) {
      setSelectedGallery(gallery);
      setFormData({
        title: gallery.title || "",
        description: gallery.description || "",
        cover_image_id: gallery.cover_image_id || "",
        cover_image_url: gallery.cover_image_url || "",
        image_ids: gallery.image_ids || [],
        images: gallery.images || [],
        location: gallery.location || "",
        date_taken: gallery.date_taken || "",
        status: gallery.status || "draft",
      });
    } else {
      setSelectedGallery(null);
      setFormData({
        title: "",
        description: "",
        cover_image_id: "",
        cover_image_url: "",
        image_ids: [],
        images: [],
        location: "",
        date_taken: "",
        status: "draft",
      });
    }
    setDialogOpen(true);
  };

  const handleOpenMediaDialog = (mode) => {
    setMediaSelectMode(mode);
    fetchMedia();
    setMediaDialogOpen(true);
  };

  const handleMediaSelect = (media) => {
    let imageUrl = media.url;
    if (imageUrl && imageUrl.startsWith('/uploads/')) {
      imageUrl = `${process.env.REACT_APP_BACKEND_URL}/api${imageUrl}`;
    }

    if (mediaSelectMode === "cover") {
      setFormData({
        ...formData,
        cover_image_id: media.media_id,
        cover_image_url: imageUrl,
      });
      setMediaDialogOpen(false);
    } else {
      // Add to images array
      if (!formData.image_ids.includes(media.media_id)) {
        setFormData({
          ...formData,
          image_ids: [...formData.image_ids, media.media_id],
          images: [...formData.images, { id: media.media_id, url: imageUrl, title: media.title }],
        });
      }
    }
  };

  const handleRemoveImage = (mediaId) => {
    setFormData({
      ...formData,
      image_ids: formData.image_ids.filter((id) => id !== mediaId),
      images: formData.images.filter((img) => img.id !== mediaId),
    });
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error("Solo se permiten imágenes");
      return;
    }

    setUploading(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      formDataUpload.append('title', file.name.replace(/\.[^/.]+$/, ''));

      const response = await axios.post(`${API}/media/upload`, formDataUpload, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' }
      });

      toast.success("Imagen subida");
      await fetchMedia();
      handleMediaSelect(response.data);
    } catch (error) {
      toast.error("Error al subir imagen");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title) {
      toast.error("El título es requerido");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        title: formData.title,
        description: formData.description || null,
        cover_image_id: formData.cover_image_id || null,
        image_ids: formData.image_ids,
        location: formData.location || null,
        date_taken: formData.date_taken || null,
        status: formData.status,
      };

      if (selectedGallery) {
        await axios.put(`${API}/galleries/${selectedGallery.gallery_id}`, payload, { headers });
        toast.success("Galería actualizada");
      } else {
        await axios.post(`${API}/galleries`, payload, { headers });
        toast.success("Galería creada");
      }

      setDialogOpen(false);
      fetchGalleries();
    } catch (error) {
      toast.error("Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedGallery) return;
    try {
      await axios.delete(`${API}/galleries/${selectedGallery.gallery_id}`, { headers });
      toast.success("Galería eliminada");
      setDeleteDialogOpen(false);
      fetchGalleries();
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-brand-text">Galerías</h1>
            <p className="text-brand-textSecondary">Gestiona las galerías de fotos</p>
          </div>
          <Button onClick={() => handleOpenDialog()} data-testid="new-gallery-btn">
            <Plus className="h-4 w-4 mr-2" />
            Nueva Galería
          </Button>
        </div>

        {/* Gallery List */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-white rounded-xl p-4 animate-pulse">
                <div className="aspect-video bg-gray-200 rounded-lg mb-3" />
                <div className="h-5 bg-gray-200 rounded w-3/4 mb-2" />
                <div className="h-4 bg-gray-200 rounded w-1/2" />
              </div>
            ))}
          </div>
        ) : galleries.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {galleries.map((gallery) => (
              <div
                key={gallery.gallery_id}
                className="bg-white rounded-xl overflow-hidden shadow-sm border"
              >
                <div className="aspect-video bg-gray-100 relative">
                  {gallery.cover_image_url ? (
                    <img
                      src={getImageUrl(gallery.cover_image_url)}
                      alt={gallery.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Images className="h-12 w-12 text-gray-300" />
                    </div>
                  )}
                  <div className="absolute top-2 right-2 px-2 py-1 bg-black/50 rounded text-white text-xs">
                    {gallery.image_count || 0} fotos
                  </div>
                </div>
                <div className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <h3 className="font-semibold text-brand-text">{gallery.title}</h3>
                      <p className="text-sm text-brand-textMuted">
                        {gallery.status === "published" ? "Publicada" : "Borrador"}
                      </p>
                    </div>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleOpenDialog(gallery)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          setSelectedGallery(gallery);
                          setDeleteDialogOpen(true);
                        }}
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12 bg-white rounded-xl">
            <Images className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No hay galerías creadas</p>
          </div>
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedGallery ? "Editar Galería" : "Nueva Galería"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Título *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Nombre de la galería"
              />
            </div>

            <div>
              <Label>Descripción</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Descripción de la galería"
                rows={3}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Ubicación</Label>
                <Input
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  placeholder="Ej: Choluteca"
                />
              </div>
              <div>
                <Label>Fecha</Label>
                <Input
                  value={formData.date_taken}
                  onChange={(e) => setFormData({ ...formData, date_taken: e.target.value })}
                  placeholder="Ej: Enero 2024"
                />
              </div>
            </div>

            {/* Cover Image */}
            <div>
              <Label>Imagen de Portada</Label>
              {formData.cover_image_url ? (
                <div className="relative mt-2">
                  <img
                    src={getImageUrl(formData.cover_image_url)}
                    alt="Cover"
                    className="w-full aspect-video object-cover rounded-lg"
                  />
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    className="absolute bottom-2 right-2"
                    onClick={() => handleOpenMediaDialog("cover")}
                  >
                    Cambiar
                  </Button>
                </div>
              ) : (
                <Button
                  type="button"
                  variant="outline"
                  className="w-full mt-2"
                  onClick={() => handleOpenMediaDialog("cover")}
                >
                  Seleccionar portada
                </Button>
              )}
            </div>

            {/* Gallery Images */}
            <div>
              <Label>Fotos de la Galería ({formData.images.length})</Label>
              <div className="mt-2 grid grid-cols-4 gap-2">
                {formData.images.map((img) => (
                  <div key={img.id} className="relative aspect-square">
                    <img
                      src={getImageUrl(img.url)}
                      alt=""
                      className="w-full h-full object-cover rounded-lg"
                    />
                    <button
                      type="button"
                      onClick={() => handleRemoveImage(img.id)}
                      className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => handleOpenMediaDialog("images")}
                  className="aspect-square border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center text-gray-400 hover:border-primary hover:text-primary transition-colors"
                >
                  <Plus className="h-6 w-6" />
                  <span className="text-xs">Agregar</span>
                </button>
              </div>
            </div>

            <div>
              <Label>Estado</Label>
              <Select
                value={formData.status}
                onValueChange={(value) => setFormData({ ...formData, status: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Borrador</SelectItem>
                  <SelectItem value="published">Publicada</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={saving}>
                {saving ? "Guardando..." : "Guardar"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Media Selection Dialog */}
      <Dialog open={mediaDialogOpen} onOpenChange={setMediaDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {mediaSelectMode === "cover" ? "Seleccionar Portada" : "Agregar Fotos"}
            </DialogTitle>
          </DialogHeader>

          {/* Upload */}
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center mb-4">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileUpload}
              className="hidden"
              id="gallery-upload"
            />
            <label htmlFor="gallery-upload" className="cursor-pointer">
              {uploading ? (
                <Loader2 className="h-8 w-8 mx-auto text-primary animate-spin" />
              ) : (
                <>
                  <Upload className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                  <span className="text-sm text-gray-600">Subir nueva imagen</span>
                </>
              )}
            </label>
          </div>

          {/* Media Grid */}
          <div className="grid grid-cols-4 gap-3">
            {mediaList.map((media) => {
              const isSelected = mediaSelectMode === "images" && formData.image_ids.includes(media.media_id);
              return (
                <button
                  key={media.media_id}
                  type="button"
                  onClick={() => handleMediaSelect(media)}
                  className={`aspect-square rounded-lg overflow-hidden border-2 transition-colors ${
                    isSelected ? "border-primary ring-2 ring-primary/20" : "border-transparent hover:border-primary"
                  }`}
                >
                  <img
                    src={getImageUrl(media.url)}
                    alt={media.title}
                    className="w-full h-full object-cover"
                  />
                </button>
              );
            })}
          </div>

          {mediaSelectMode === "images" && (
            <div className="flex justify-end pt-4">
              <Button onClick={() => setMediaDialogOpen(false)}>Listo</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar galería?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Se eliminará la galería permanentemente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
        <AdminPagination
          page={page}
          total={total}
          limit={limit}
          onPageChange={setPage}
          onLimitChange={(l) => { setLimit(l); setPage(1); }}
          testId="galerias-pagination"
          label="galerías"
        />
    </AdminLayout>
  );
}
